import {
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
} from "react";
import { useSetAtom } from "jotai";
import { Icon, TextInput, Text } from "@zapier/design-system";
import {
  closeSecondaryToolbarMenuAtom,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import styles from "./FloatingLinkInput.module.css";

type Props = {
  link: string | undefined;
};

// Saving to BE will fail if URL is not valid
function isValidUrl(url: string | undefined): boolean {
  if (!url) return true; // Allow empty/undefined

  try {
    const urlObj = new URL(url);
    // Check for valid scheme (http, https, ftp)
    const validSchemes = ["http:", "https:", "ftp:"];
    if (!validSchemes.includes(urlObj.protocol)) {
      return false;
    }
    // Check for valid domain (at least one character)
    if (!urlObj.hostname) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function FloatingLinkInput({ link }: Props) {
  const [currentLink, setCurrentLink] = useState<string | undefined>(link);
  const [isError, setIsError] = useState(false);
  const latestLinkRef = useRef(currentLink);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuAtom);

  useEffect(() => {
    latestLinkRef.current = currentLink;
  }, [currentLink]);

  useEffect(() => {
    return () => {
      // Using unmount instead of blur because clicking the React Flow pane unmounts before blur fires
      if (latestLinkRef.current !== link && isValidUrl(latestLinkRef.current)) {
        takeSnapshot();
        updateNodeAndSave({
          data: {
            link: latestLinkRef.current,
          },
        });
      }
    };
  }, [link, takeSnapshot, updateNodeAndSave]);

  const validateAndSave = useCallback(() => {
    const isValid = isValidUrl(currentLink);
    setIsError(!isValid);

    if (isValid && currentLink !== link) {
      takeSnapshot();
      updateNodeAndSave({
        data: { link: currentLink },
      });

      closeSecondaryMenu();
    }
  }, [closeSecondaryMenu, currentLink, link, takeSnapshot, updateNodeAndSave]);

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCurrentLink(newValue);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        validateAndSave();
      }
    },
    [validateAndSave],
  );

  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <div className={styles.floatingTypeaheadWrapper}>
          <TextInput
            name="url"
            placeholder="Type or paste in URL"
            inputMode="url"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            onBlur={validateAndSave}
            value={currentLink}
            isErrored={isError}
            renderIconBefore={() => <Icon name="actionLink" />}
          />
          {isError && (
            <div className={styles.errorContainer}>
              <Icon color="StatusErrorStronger" name="formXCircle" size={18} />
              <Text color="TextInverted" type="MinimalPrint1">
                Please enter a valid URL.
              </Text>
            </div>
          )}
        </div>
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
